import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import {
  normalizeTimeOffCalendarChangedNotice,
  TIME_OFF_CALENDAR_CHANGED_EVENT,
  TIME_OFF_CALENDAR_HUB_PATH,
  type TimeOffCalendarChangedNotice
} from './timeOffCalendarRealtimeTypes';

export interface TimeOffCalendarClientOptions {
  onCalendarChanged: (notice: TimeOffCalendarChangedNotice) => void;
  onConnectedChange?: (connected: boolean) => void;
}

export interface TimeOffCalendarClient {
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

/**
 * SignalR client for HR time-off calendar pushes. Same-origin cookie auth; Vite dev proxy
 * forwards /hubs with the WebSocket upgrade.
 */
export function createTimeOffCalendarClient(options: TimeOffCalendarClientOptions): TimeOffCalendarClient {
  const { onCalendarChanged, onConnectedChange } = options;

  let connection: HubConnection | null = null;
  let disposed = false;

  const leaveNow = () => {
    const conn = connection;
    if (!conn || conn.state !== HubConnectionState.Connected) return;
    conn.invoke('LeaveCalendarAsync').catch(() => undefined);
  };

  const onPageHide = () => leaveNow();

  const join = async (conn: HubConnection) => {
    try {
      await conn.invoke('JoinCalendarAsync');
    } catch {
      /* reconnect will retry */
    }
  };

  const start = async () => {
    if (connection || disposed) return;

    const conn = new HubConnectionBuilder()
      .withUrl(TIME_OFF_CALENDAR_HUB_PATH, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on(TIME_OFF_CALENDAR_CHANGED_EVENT, (notice: unknown) => {
      onCalendarChanged(normalizeTimeOffCalendarChangedNotice(notice));
    });

    conn.onreconnected(() => {
      onConnectedChange?.(true);
      void join(conn);
    });
    conn.onreconnecting(() => onConnectedChange?.(false));
    conn.onclose(() => onConnectedChange?.(false));

    window.addEventListener('pagehide', onPageHide);
    connection = conn;
    try {
      await conn.start();
      if (disposed) {
        await conn.stop();
        return;
      }
      onConnectedChange?.(true);
      await join(conn);
    } catch {
      onConnectedChange?.(false);
    }
  };

  const stop = async () => {
    disposed = true;
    window.removeEventListener('pagehide', onPageHide);
    const conn = connection;
    connection = null;
    if (!conn) return;
    try {
      if (conn.state === HubConnectionState.Connected) {
        await conn.invoke('LeaveCalendarAsync').catch(() => undefined);
      }
    } finally {
      await conn.stop().catch(() => undefined);
    }
  };

  return { start, stop };
}
